import { FilterDescriptor, State, process } from '@progress/kendo-data-query';
import { DropDownListChangeEvent } from '@progress/kendo-react-dropdowns';
import {
  GridDataStateChangeEvent,
  GridHeaderSelectionChangeEvent,
  GridPagerSettings,
  GridSelectionChangeEvent,
} from '@progress/kendo-react-grid';
import { GridSelectableSettings } from '@progress/kendo-react-grid/dist/npm/interfaces/GridSelectableSettings';
import { AxiosResponse } from 'axios';
import { Dispatch, FC, SetStateAction, useCallback, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { useMemberCategories } from 'src/assets/static/view_department/useDepartmentCategories';
import { useMemberColumn } from 'src/assets/static/view_department/useMemberColumn';
import GridWrapper from 'src/components/GridWrapper';
import useViewDepartment from 'src/hooks/useViewDepartments';
import useWindowSize from 'src/hooks/useWindowSize';
import { BulkUpdateMemberRequest } from 'src/services/useBulkUpdateMemberApi';
import { ClientResponse } from 'src/services/useGetClientApi';
import { Department } from 'src/services/useGetDepartmentApi';
import { modifyFilters } from 'src/utils/requestInterceptor';
import { initialDepartmentState } from '../ViewDepartmentContext';
import { viewDepartmentFilters } from '../ViewDepartmentFilters';
import BulkUpdateTableToolbar from './BulkUpdateTableToolbar';

const selectable: GridSelectableSettings = {
  enabled: false,
  drag: false,
  cell: false,
};

const gridPagerSettings: GridPagerSettings = {
  buttonCount: 1,
  pageSizes: [10, 20, 50, 100, 500],
};

const defaultProps = {
  tableTextId: 'Members',
  DataItemKey: 'rowId',
  sortable: true,
  resizable: true,
  showColumnMenu: true,
  selectable: selectable,
  pageable: gridPagerSettings,
} as const;

const initialAssignMemberDataState: State = {
  skip: 0,
  take: 500,
  sort: [],
  filter: {
    logic: 'and',
    filters: [],
  },
};
export interface IAssignMember extends ClientResponse {
  selected: boolean;
}

interface AssignMemberTableProps {
  handlePaneClose: () => void;
  selectedDepartments: Department[];
  assignMember: IAssignMember[];
  setAssignMember: Dispatch<SetStateAction<IAssignMember[]>>;
  bulkUpdateMember: (
    params?: BulkUpdateMemberRequest | undefined
  ) => Promise<AxiosResponse<any, any>>;
  isBulkUpdateMemberLoading: boolean;
}

const AssignMemberTable: FC<AssignMemberTableProps> = ({
  handlePaneClose,
  selectedDepartments,
  assignMember,
  setAssignMember,
  bulkUpdateMember,
  isBulkUpdateMemberLoading,
}) => {
  const { height } = useWindowSize();
  const dropDownList = useMemberCategories();
  const { refreshTable, t, setDepartmentsDataState } = useViewDepartment();
  const assignDepartmentColumns = useMemberColumn();

  const [state, setState] = useState(initialAssignMemberDataState);
  const [selectionFilter, setSelectionFilter] = useState(dropDownList[1]);

  const selectedRows = useMemo(
    () => assignMember.filter((member) => member.selected),
    [assignMember]
  );

  const getMemberFilters = useCallback(
    (state: State) => {
      const filters: FilterDescriptor[] = modifyFilters(state.filter, undefined, false);
      return [...filters, ...(viewDepartmentFilters[selectionFilter.value]?.filters || [])];
    },
    [selectionFilter.value]
  );

  const { data: processedMembers, total } = useMemo(
    () =>
      process(assignMember, {
        ...state,
        filter: {
          logic: 'and',
          filters: getMemberFilters(state),
        },
      }) as {
        data: IAssignMember[];
        total: number;
      },
    [assignMember, getMemberFilters, state]
  );

  const onCheckbookSelectionChange = useCallback(
    (event: GridSelectionChangeEvent) => {
      setAssignMember((prev) => {
        const prevItems = [...prev].map((item) =>
          item.rowId === event?.dataItem.rowId ? { ...item, selected: !item.selected } : item
        );
        return prevItems;
      });
    },
    [setAssignMember]
  );

  const changeDataState = useCallback(
    (e: GridDataStateChangeEvent) => {
      setAssignMember((prev) => prev?.map((row) => ({ ...row, selected: false })));
      setState(e.dataState);
    },
    [setAssignMember]
  );

  const onHeaderSelectionChange = useCallback(
    (event: GridHeaderSelectionChangeEvent) => {
      const checkboxElement: any = event?.syntheticEvent?.target;
      const { checked } = checkboxElement;
      setAssignMember((prev) => prev?.map((row) => ({ ...row, selected: checked })));
    },
    [setAssignMember]
  );

  const handleClearSelection = useCallback(() => {
    setAssignMember((prev) => prev?.map((row) => ({ ...row, selected: false })));
  }, [setAssignMember]);

  const handleFilterChange = useCallback(
    (e: DropDownListChangeEvent) => {
      setAssignMember((prev) => prev?.map((row) => ({ ...row, selected: false })));
      setSelectionFilter(e.value);
    },
    [setAssignMember]
  );

  const handleAssignMember = useCallback(async () => {
    const departments = selectedDepartments.map((department) => department.departmentId);
    const clients = processedMembers
      ?.filter((member) => member.selected)
      ?.map((member) => member.clientId);
    bulkUpdateMember({ departments, clients, assign: true })
      .then(async (res) => {
        toast.success(t('Members added to departments successfully'));
        await refreshTable(true);
        handlePaneClose();

        setDepartmentsDataState(initialDepartmentState);
      })
      .catch(() => {
        toast.success(t('Failed to add members to departments'));
      });
  }, [
    bulkUpdateMember,
    processedMembers,
    refreshTable,
    selectedDepartments,
    setDepartmentsDataState,
    handlePaneClose,
    t,
  ]);

  const gridToolbarChildren = useMemo(
    () => (
      <BulkUpdateTableToolbar
        isAssign={true}
        isMember={true}
        selectionFilter={selectionFilter}
        dropDownList={dropDownList}
        handleClearSelection={handleClearSelection}
        selectedRows={selectedRows}
        handleFilterChange={handleFilterChange}
        handleUpdate={handleAssignMember}
        selectedDepartment={selectedDepartments}
        isLoading={isBulkUpdateMemberLoading}
      />
    ),
    [
      dropDownList,
      handleAssignMember,
      handleClearSelection,
      handleFilterChange,
      isBulkUpdateMemberLoading,
      selectedDepartments,
      selectedRows,
      selectionFilter,
    ]
  );

  return (
    <main className="p-[10px] bg-[#ffffff]">
      <GridWrapper
        id="assignMemberTable"
        columns={assignDepartmentColumns}
        dataSource={processedMembers}
        dataState={state}
        total={total}
        height={`${height - 320}px`}
        gridToolbarChildren={gridToolbarChildren}
        onDataStateChange={changeDataState}
        {...defaultProps}
        onHeaderSelectionChange={onHeaderSelectionChange}
        onSelectionChange={onCheckbookSelectionChange}
        noRecordText={t('No records to display')}
        loading={isBulkUpdateMemberLoading}
        hideScrollBar
      />
    </main>
  );
};

export default AssignMemberTable;
